import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { PaletteMode } from "@mui/material";

// Extend the theme interface to include custom text properties
declare module "@mui/material/styles" {
  interface Theme {
    customText: {
      primary: {
        main: string;
        dark: string;
      };
      secondary: {
        main: string;
        dark: string;
      };
    };
  }
  interface ThemeOptions {
    customText?: {
      primary?: {
        main?: string;
        dark?: string;
      };
      secondary?: {
        main?: string;
        dark?: string;
      };
    };
  }
}

// Define the light/dark mode color palettes
const lightPalette = {
  primary: {
    main: "#174B67",
    light: "#4F7C96",
    dark: "#0E3447",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#2BB3A3",
    light: "#5ED4C4",
    dark: "#1A877C",
    contrastText: "#FFFFFF",
  },
  background: {
    default: "#F9FAFB",
    paper: "#FFFFFF",
  },
  text: {
    primary: "#102A43",
    secondary: "#627D98",
    disabled: "#9FB3C8",
  },
  customText: {
    primary: {
      main: "#102A43",
      dark: "#0A1E2E",
    },
    secondary: {
      main: "#627D98",
      dark: "#4A5D73",
    },
  },
  error: {
    main: "#D64545",
    light: "#F29B9B",
    dark: "#972D2D",
    contrastText: "#FFFFFF",
  },
  success: {
    main: "#2D9C6B",
    light: "#60C199",
    dark: "#1E704B",
    contrastText: "#FFFFFF",
  },
  info: {
    main: "#3BAFDA",
    light: "#7FD2F1",
    dark: "#2889A9",
    contrastText: "#FFFFFF",
  },
};

const darkPalette = {
  primary: {
    main: "#4F7C96",
    light: "#6A95AD",
    dark: "#174B67",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#5ED4C4",
    light: "#8EEAE0",
    dark: "#2BB3A3",
    contrastText: "#000000",
  },
  background: {
    default: "#102A43",
    paper: "#1E3A5F",
  },
  text: {
    primary: "#F0F4F8",
    secondary: "#BCCCDC",
    disabled: "#829AB1",
  },
  customText: {
    primary: {
      main: "#F0F4F8",
      dark: "#E2E9F0",
    },
    secondary: {
      main: "#BCCCDC",
      dark: "#A5B8CF",
    },
  },
  error: {
    main: "#F29B9B",
    light: "#FFBDBD",
    dark: "#D64545",
    contrastText: "#000000",
  },
  success: {
    main: "#60C199",
    light: "#8FD3B6",
    dark: "#2D9C6B",
    contrastText: "#000000",
  },
  info: {
    main: "#7FD2F1",
    light: "#AFEAFF",
    dark: "#3BAFDA",
    contrastText: "#000000",
  },
};

// Create theme with the specified mode
const createAppTheme = (mode: PaletteMode) => {
  return createTheme({
    palette: {
      mode,
      ...(mode === "light" ? lightPalette : darkPalette),
    },
    typography: {
      fontFamily: [
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
      h1: {
        fontSize: "2.5rem",
        fontWeight: 500,
      },
      h2: {
        fontSize: "2rem",
        fontWeight: 500,
      },
      h3: {
        fontSize: "1.75rem",
        fontWeight: 500,
      },
      h4: {
        fontSize: "1.5rem",
        fontWeight: 500,
      },
      h5: {
        fontSize: "1.25rem",
        fontWeight: 500,
      },
      h6: {
        fontSize: "1rem",
        fontWeight: 500,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          },
        },
      },
    },
  });
};

// Create the default light theme
const theme = responsiveFontSizes(createAppTheme("light"));

export default theme;
export { createAppTheme };
